import { makeStyles } from '@material-ui/core/styles';
import 'react-grid-layout/css/styles.css';

export const innerSpacing = 2;
export const outerSpacing = 1;
export const titleFontSize = 14;
export const titleFontWeight = 'bold';
export const titleLineHeight = 1.43;
export const loaderHeight = 1;
export const stateSummaryFontSize = 10;
export const stateSummaryLineHeight = 1.66;

export function calcPanelHeight(theme, showTitle, showStateSummary, contentHeight) {
    const titleHeight = showTitle ? titleFontSize * titleLineHeight + theme.spacing(innerSpacing * 2) : 0;
    const stateSummaryHeight = showStateSummary ? stateSummaryFontSize * stateSummaryLineHeight : 0;
    const contentPadding = showTitle ? theme.spacing(innerSpacing) : theme.spacing(innerSpacing * 2);
    const cardPadding = theme.spacing(outerSpacing);

    return Math.ceil(titleHeight + loaderHeight + stateSummaryHeight + contentPadding + cardPadding + contentHeight);
}

const useStyles = makeStyles((theme) => {
    const dragHandleColor = theme.palette.action.active;

    return {
        panel: {
            '& .MuiCardHeader-root': {
                fontSize: titleFontSize,
                lineHeight: titleLineHeight,
                padding: theme.spacing(innerSpacing),
            },

            '& .MuiCardHeader-content': {
                ...theme.mixins.flex.displayFlex,
                ...theme.mixins.flex.grow(1),
                minWidth: 0,
            },

            '& .MuiCardHeader-title': {
                ...theme.mixins.flex.displayFlex,
                ...theme.mixins.flex.grow(1),
                minWidth: 0,
                fontSize: titleFontSize,
                lineHeight: titleLineHeight,
                fontWeight: titleFontWeight,
            },

            '& .MuiLinearProgress-root': {
                height: loaderHeight,
            },

            '& .MuiCardContent-root': {
                padding: 0,
                margin: theme.spacing(innerSpacing),
                marginTop: 0,
                overflow: 'hidden',
            },

            '&:hover $panelActions, &:hover $panelActionPortal': {
                opacity: 1,
                width: 'auto',
                visibility: 'visible',
            },
        },

        panelTitle: {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            cursor: 'default',
            marginBottom: 2,
        },

        panelStateSummary: {
            ...theme.mixins.flex.displayFlex,
            ...theme.mixins.flex.justifyContentCenter,
            ...theme.mixins.flex.flexWrapWrap,
            ...theme.mixins.flex.shrink(0),
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(1),
            minHeight: 20,

            '& #optional-filter-menu-button': {
                padding: `0 ${theme.spacing(0.5)}px`,
                marginRight: theme.spacing(1),

                '& .MuiSvgIcon-fontSizeSmall': {
                    fontSize: 14,
                },
            },
        },

        panelStateSummaryItem: {
            marginRight: theme.spacing(2),

            '& .MuiTypography-caption': {
                fontSize: stateSummaryFontSize,
                lineHeight: stateSummaryLineHeight,
            },
        },

        panelStateSummaryItemTitle: {
            whiteSpace: 'nowrap',
            fontWeight: 600,
        },

        panelStateSummaryItemValue: {
            whiteSpace: 'nowrap',
        },

        filterSummary: {
            ...theme.mixins.flex.displayInlineFlex,
            cursor: 'pointer',
        },

        disabledFilterSummary: {
            cursor: 'default',

            '& $filterValue': {
                borderBottom: 'none',
                marginBottom: 2,

                '&:hover': {
                    borderBottom: 'none',
                    marginBottom: 2,
                },
            },
        },

        filter: {
            margin: `0 ${theme.spacing(1)}px 0 0`,
        },

        filterLabel: {
            fontSize: stateSummaryFontSize,
            lineHeight: stateSummaryLineHeight,
            fontWeight: 'bold',

            '&::after': {
                content: '":"',
                fontSize: stateSummaryFontSize,
                lineHeight: stateSummaryLineHeight,
                fontWeight: 'bold',
                marginRight: theme.spacing(0.5),
            },
        },

        filterValue: {
            fontSize: stateSummaryFontSize,
            lineHeight: stateSummaryLineHeight,
            borderBottom: '1px solid rgba(0, 0, 0, 0.4)',
            marginBottom: 1,

            '&:hover': {
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                marginBottom: 0,
            },
        },

        filterButton: {
            margin: `${theme.spacing(0.25)}px 0 0 ${theme.spacing(0.5)}px`,
            padding: 0,

            '&:hover': {
                backgroundColor: 'inherit',
            },

            '& svg': {
                fontSize: '1rem',
            },
        },

        hiddenTitle: {
            '& .MuiCardContent-root': {
                marginTop: theme.spacing(innerSpacing),
            },
        },

        visibleSummary: {
            '& .MuiCardHeader-root': {
                paddingBottom: 0,
            },
        },

        panelCard: {
            margin: theme.spacing(outerSpacing / 2),
        },

        panelActions: {
            transitionProperty: 'opacity',
            transitionDuration: '0.2s',
            transitionTimingFunction: 'ease',
            opacity: 0,
            width: 0,
            position: 'relative',
            top: theme.spacing(0.75),
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),

            '& .MuiIconButton-root': {
                padding: 0,
                marginLeft: theme.spacing(1),
            },
        },

        panelActionPortal: {
            transitionProperty: 'opacity',
            transitionDuration: '0.2s',
            transitionTimingFunction: 'ease',
            opacity: 0,
            width: 0,
            maxHeight: 20,
            visibility: 'hidden',
            position: 'relative',
            top: theme.spacing(0.75),
            '& .matrixEditingActions': {
                position: 'absolute',
                left: theme.spacing(-2),
                top: theme.spacing(-3) - 1,
                marginLeft: theme.spacing(0),
                marginRight: theme.spacing(1),
                '& .MuiButtonBase-root': {
                    padding: 2,
                    marginLeft: theme.spacing(1),
                },
            },
            '@media print': {
                opacity: 1,
                width: 'auto',
                visibility: 'visible',
            },
        },

        progressPlaceholder: {
            minHeight: loaderHeight,
        },

        panelComponentContainer: {
            position: 'relative',
        },

        dragHandle: {},

        draggablePanel: {
            '& .MuiCardHeader-root': {
                '& .MuiCardHeader-title': {
                    marginBottom: theme.spacing(-1),
                },

                '& $dragHandle': {
                    ...theme.mixins.flex.displayFlex,
                    cursor: 'grab',
                    borderLeft: `4px dotted ${dragHandleColor}`,
                    paddingLeft: theme.spacing(0.25),
                    height: theme.spacing(2),
                    position: 'relative',
                    top: theme.spacing(0.25),

                    '& span': {
                        borderLeft: `4px dotted ${dragHandleColor}`,
                        paddingLeft: theme.spacing(1),
                    },
                },

                '& .MuiTextField-root': {
                    top: theme.spacing(-1),
                    transitionProperty: 'padding-right',
                    transitionDuration: '0.1s',
                    transitionTimingFunction: 'ease',
                    paddingRight: theme.spacing(1),
                },

                '& .MuiInputLabel-root': {
                    fontSize: '0.75rem',
                    top: theme.spacing(-0.25),
                },

                '& .MuiInputBase-input': {
                    fontSize: titleFontSize,
                    fontWeight: titleFontWeight,
                    paddingTop: 0,
                    paddingBottom: 3,
                    marginTop: theme.spacing(-0.75),
                },

                '& .MuiCardHeader-action': {
                    marginRight: 0,
                },
            },
        },

        hiddenPanel: {
            display: 'none',
        },

        propertiesPanel: {
            display: 'flex',
            flexDirection: 'row',
            '& .MuiTableCell-root': {
                fontSize: '11px !important',
            },
        },
    };
});

export default useStyles;
